<template>
  <slot
    :selected-variant="selectedVariant"
    :select-size="selectSize"
    :select-flavor="selectFlavor"
    :is-active-size="isActiveSize"
    :is-active-flavor="isActiveFlavor"
    :current-price="currentPrice"
    :compare-price="comparePrice"
    :display-price="displayPrice"
    :display-compare-price="displayComparePrice"
    :variant-image="variantImage"
    :hover-image="hoverImage"
    :variant-title="variantTitle"
    :variant-url="variantUrl"
    :add-to-cart="handleAddToCart"
    :loading="loading"
    :size-options="sizeOptions"
    :flavor-options="flavorOptions"
    :size-label="sizeLabel"
    :flavor-label="flavorLabel"
    :selector-items="selectorItems"
    :selector-label="selectorLabel"
    :active-handle="activeHandle"
    :active-flavor-label="activeFlavorLabel"
    :select-linked-flavor="selectLinkedFlavor"
    :is-active-handle="isActiveHandle"
    :is-available-handle="isAvailableHandle"
    :add-error="addError"
    :store-locator-url="storeLocatorUrl"
  />
</template>

<script>
export default {
  name: "PlpProductCard",
};
</script>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount} from "vue";
import {useStore} from "vuex";
import JSON5 from "json5";
import {formatMoney} from "@shopify/theme-currency";
import {dispatchCartErrorToast} from "@/utils/cart-error-toast";
import {optimisticFromProduct} from "@/utils/optimistic-line";

const props = defineProps({
  product: {
    type: String,
    required: true,
  },
  defaultVariantId: {
    type: Number,
    default: 0,
  },
  selectorItems: {
    type: Array,
    default: () => [],
  },
  selectorLabel: {
    type: String,
    default: "",
  },
});

const store = useStore();
const loading = ref(false);
const addError = ref("");
const storeLocatorUrl = "/pages/store-locator";

// Decode URL-encoded values (handles + as space)
const decodeEntities = str =>
  String(str)
    .replace(/&#39;/g, "'")
    .replace(/&#34;/g, '"')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

const decode = val =>
  val
    ? decodeEntities(decodeURIComponent(String(val).replace(/\+/g, " ")))
    : "";

// Parse initial product data
const initialProduct = JSON5.parse(props.product);

// Reactive product — swaps when linked flavor is selected
const activeProduct = ref(initialProduct);
const activeHandle = ref(initialProduct.handle || "");

// Reactive variants derived from active product
const variants = computed(() => activeProduct.value.variants || []);


// Variants come from product options (any name). Flavor is driven by the
// `selector_skus` metafield (linked products), handled separately outside
// this component — it does NOT come from product options.
const sizeOptionIndex = computed(() => {
  const options = activeProduct.value.options || [];
  return options.length > 0 ? 0 : -1;
});

const flavorOptionIndex = computed(() => -1);

// Selected option values
const selectedSize = ref("");
const selectedFlavor = ref("");

// Get option key for a given index
const getOptionKey = idx => `option${idx + 1}`;

// Get option value from variant at given option index
const getVariantOptionVal = (variant, optIdx) => {
  const key = getOptionKey(optIdx);
  return decode(variant[key] || "");
};

// Initialize selections from a product's default variant.
//
// The merchandised default (`helper-default-variant`, usually Medium) only
// wins while it is in stock — otherwise the card would open on a sold-out
// pill, which is now disabled, and the only CTA left would be "Find Store".
const initSelections = prod => {
  const vars = prod.variants || [];
  const preferred = vars.find(v => v.id === props.defaultVariantId);
  const defVariant =
    (preferred && preferred.available && preferred) ||
    vars.find(v => v.available) ||
    preferred ||
    vars[0];

  if (!defVariant) return;

  const options = prod.options || [];
  if (options.length > 0) {
    selectedSize.value = getVariantOptionVal(defVariant, 0);
  }
};

// Init on mount
initSelections(initialProduct);

// Size abbreviation helper
const abbreviateSize = size => {
  const map = {
    "extra small": "XS",
    mini: "XS",
    small: "S",
    medium: "M",
    large: "L",
    "extra large": "XL",
    "one size": "OS",
  };
  const lower = size.toLowerCase().trim();
  if (map[lower]) return map[lower];
  const numericMatch = lower.match(/^(\d+(?:\.\d+)?)\s*[a-z]*$/i);
  if (numericMatch) {
    return numericMatch[1];
  }
  // Multi-word: take first letter of each word, max 3 chars
  // Split on spaces, hyphens, parentheses, slashes
  const words = size.trim().split(/[\s\-\/\(\)]+/).filter(w => w.length > 0);
  if (words.length >= 2) {
    return words.slice(0, 3).map(w => w[0].toUpperCase()).join("");
  }
  return size.substring(0, 3);
};

// Flavor color map (fallback for variant-based flavors)
const flavorColorMap = {
  tuna: "#E8A87C",
  chicken: "#F5C542",
  salmon: "#FA8072",
  beef: "#8B4513",
  peanut: "#D4A574",
};

// Build option arrays
const sizeOptions = computed(() => {
  if (sizeOptionIndex.value < 0) return [];
  const optKey = getOptionKey(sizeOptionIndex.value);
  const seen = new Set();
  const options = [];

  variants.value.forEach(v => {
    const rawVal = v[optKey];
    if (!rawVal) return;
    const val = decode(rawVal);
    if (seen.has(val)) return;
    seen.add(val);
    options.push({
      value: val,
      abbrev: abbreviateSize(val),
      available: variants.value.some(
        variant =>
          decode(variant[optKey] || "") === val && variant.available,
      ),
    });
  });

  // When linked flavor products are wired up, cap to the first two sizes
  // (e.g. Snack / Full Size) so the row doesn't duplicate per flavor.
  if (props.selectorItems && props.selectorItems.length > 0) {
    return options.slice(0, 2);
  }

  return options;
});

const flavorOptions = computed(() => {
  if (flavorOptionIndex.value < 0) return [];
  const optKey = getOptionKey(flavorOptionIndex.value);
  const seen = new Set();
  const options = [];

  variants.value.forEach(v => {
    const rawVal = v[optKey];
    if (!rawVal) return;
    const val = decode(rawVal);
    if (seen.has(val)) return;
    seen.add(val);
    const lower = val.toLowerCase();
    options.push({
      value: val,
      color: flavorColorMap[lower] || "#ccc",
      image: null,
      available: variants.value.some(
        variant =>
          decode(variant[optKey] || "") === val && variant.available,
      ),
    });
  });

  return options;
});

// Selected variant computed
const selectedVariant = computed(() => {
  return variants.value.find(v => {
    let match = true;
    if (sizeOptionIndex.value >= 0 && selectedSize.value) {
      match =
        match &&
        getVariantOptionVal(v, sizeOptionIndex.value) ===
          selectedSize.value;
    }
    if (flavorOptionIndex.value >= 0 && selectedFlavor.value) {
      match =
        match &&
        getVariantOptionVal(v, flavorOptionIndex.value) ===
          selectedFlavor.value;
    }
    return match;
  });
});

const sizeLabel = computed(() => selectedSize.value);
const flavorLabel = computed(() => selectedFlavor.value);

const currentPrice = computed(() => {
  if (selectedVariant.value) {
    return selectedVariant.value.price;
  }
  return activeProduct.value.price;
});

const comparePrice = computed(() => {
  if (selectedVariant.value) {
    return selectedVariant.value.compareAtPrice;
  }
  return activeProduct.value.compareAtPrice;
});

const displayPrice = computed(() => {
  return formatMoney(currentPrice.value);
});

const displayComparePrice = computed(() => {
  if (comparePrice.value && comparePrice.value > currentPrice.value) {
    return formatMoney(comparePrice.value);
  }
  return null;
});

// Variant image
const variantImage = computed(() => {
  const prod = activeProduct.value;
  if (selectedVariant.value) {
    if (selectedVariant.value.variantImage) {
      return selectedVariant.value.variantImage.src;
    }
    if (selectedVariant.value.productImage) {
      return selectedVariant.value.productImage.src;
    }
  }
  if (prod.featured_image) {
    return prod.featured_image.src;
  }
  return prod.images && prod.images[0] ? prod.images[0] : null;
});

// Hover image
// Priority:
// 1) selected variant's rollover_image metafield (custom.rollover_image)
// 2) product's 2nd image as fallback
const hoverImage = computed(() => {
  const prod = activeProduct.value;
  if (selectedVariant.value?.rolloverImage?.src) {
    return selectedVariant.value.rolloverImage.src;
  }
  if (prod.images && prod.images.length > 1) {
    return prod.images[1];
  }
  return null;
});

// Variant title
//
// `displayTitle` is the `custom.display_title` metafield, falling back to the
// product title in Liquid. It is product-level, so clicking a size pill does
// not rename the tile — but selecting a linked sibling does, since that swaps
// `activeProduct` outright.
const variantTitle = computed(() => {
  const prod = activeProduct.value;
  const displayTitle = decode(prod.displayTitle || "");
  if (displayTitle) return displayTitle;
  if (selectedVariant.value) {
    return (
      decode(selectedVariant.value.productTitle || "") ||
      decode(prod.title || "")
    );
  }
  return decode(prod.title || "");
});

// Product URL with variant
const variantUrl = computed(() => {
  const base = decode(activeProduct.value.url || "");
  if (selectedVariant.value) {
    return `${base}?variant=${selectedVariant.value.id}`;
  }
  return base;
});

// Actions
const selectSize = value => {
  const option = sizeOptions.value.find(o => o.value === value);
  if (option && !option.available) return;
  selectedSize.value = value;
};

const selectFlavor = value => {
  selectedFlavor.value = value;
};

const isActiveSize = value => {
  return selectedSize.value === value;
};

const isActiveFlavor = value => {
  return selectedFlavor.value === value;
};

// Linked product flavor selector
const activeFlavorLabel = computed(() => {
  const current = props.selectorItems.find(
    i => i.handle === activeHandle.value,
  );
  return current?.label || "";
});

const isActiveHandle = handle => {
  return handle === activeHandle.value;
};

// A linked sibling is a whole product (a collapsed size family), so it counts
// as buyable when any of its own variants is in stock. `available` is printed
// by Liquid; treat a missing flag as available so older markup keeps working.
const isAvailableHandle = handle => {
  const item = props.selectorItems.find(i => i.handle === handle);
  if (!item) return true;
  if (item.available === false) return false;
  const vars = item.product?.variants;
  if (!vars || !vars.length) return true;
  return vars.some(v => v.available);
};

const applyLinkedFlavor = item => {
  if (item.handle === activeHandle.value) return;
  if (!item.product) return;

  // Swap the active product
  activeProduct.value = item.product;
  activeHandle.value = item.handle;

  // Re-init size/flavor selections for the new product
  selectedSize.value = "";
  selectedFlavor.value = "";
  initSelections(item.product);
};

// Click handler: a sold-out sibling is disabled in the markup, this is the
// belt-and-braces guard. The PLP size filter uses `applyLinkedFlavor`
// directly — an explicit filter has to show the family it matched, in stock
// or not, otherwise the card would sit on the wrong size.
const selectLinkedFlavor = item => {
  if (!isAvailableHandle(item.handle)) return;
  applyLinkedFlavor(item);
};

// Sync the card's selected size variant with the active PLP size filter.
// Match the filter value to a size option (exact first, then substring).
const applyFilterSize = filterSize => {
  if (!filterSize) return;
  const target = String(filterSize).toLowerCase().trim();
  const opts = sizeOptions.value || [];
  let match = opts.find(o => o.value.toLowerCase().trim() === target);
  if (!match) {
    match = opts.find(o => o.value.toLowerCase().includes(target));
  }
  if (match) {
    selectedSize.value = match.value;
    return;
  }

  // No matching variant: in a collapsed size family each size is its own
  // product, so the size lives in `selectorItems` and the card has to swap
  // product rather than variant. The pill labels are abbreviated ("M"), the
  // filter value is not ("Medium") — compare both spellings.
  const abbrev = abbreviateSize(String(filterSize)).toLowerCase().trim();
  const norm = v => abbreviateSize(String(v || "")).toLowerCase().trim();
  const item = (props.selectorItems || []).find(i => {
    const label = String(i.label || "").toLowerCase().trim();
    const title = String(i.title || "").toLowerCase().trim();
    if (label === target || title === target) return true;
    // Normalising both sides through the abbreviation map makes "Mini",
    // "Extra Small" and "XS" the same size.
    return (
      (label !== "" && norm(label) === abbrev) ||
      (title !== "" && norm(title) === abbrev)
    );
  });
  if (item) {
    applyLinkedFlavor(item);
  }
};

const handleFilterChange = e => {
  const size = e.detail?.size;
  if (size) {
    applyFilterSize(size);
  } else {
    // Filter cleared — back to the product this tile was rendered for, since
    // the filter may have swapped it to a sibling of its size family.
    if (activeHandle.value !== initialProduct.handle) {
      activeProduct.value = initialProduct;
      activeHandle.value = initialProduct.handle || "";
    }
    initSelections(activeProduct.value);
  }
};

onMounted(() => {
  window.addEventListener("plp-filter-change", handleFilterChange);
});

onBeforeUnmount(() => {
  window.removeEventListener("plp-filter-change", handleFilterChange);
});

const handleAddToCart = async () => {
  if (!selectedVariant.value) return;

  if (!selectedVariant.value.available) {
    window.location.href = storeLocatorUrl;
    return;
  }

  // No `loading` gate and no spinner around the await: the cart shows the line
  // before this resolves, so holding the button for the round trip only made
  // the add look slow, and refusing to run while one was in flight dropped the
  // second click. `loading` stays as a prop for the card's own use.
  addError.value = "";
  try {
    await store.dispatch("cart/addItem", {
      id: selectedVariant.value.id,
      quantity: 1,
      _optimistic: {
        ...optimisticFromProduct(activeProduct.value, selectedVariant.value),
        // The picture and the strike-through the card is showing right now, so
        // the cart row opens with what was clicked rather than with whatever
        // the serializer happened to put on the variant.
        image: variantImage.value,
        compare_at_price: comparePrice.value,
      },
    });
  } catch (err) {
    addError.value = err?.message || "Unable to add to cart.";
    dispatchCartErrorToast(store, err);
  }
};
</script>
